import React, { useMemo, useState } from 'react';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { Modal } from 'react-bootstrap';
import type { GenerateError, GenerateSuccess, BenefitKey } from './types';
import { BENEFIT_OPTIONS } from './questions';
import FeedbackBar from './FeedbackBar';
import type { DideWriterEndpointBase } from './endpoints';

export type AppliedField = 'description' | 'title' | 'experience_required' | 'benefits';

export interface PreviewApplyPayload {
  draft_id: number;
  description?: string;
  // Sanitized HTML of `description` from the server, when available. Hosts
  // that store HTML (the job form, admin) should insert this.
  description_html?: string;
  title?: string;
  experience_required?: string;
  benefits: BenefitKey[];
  applied_fields: AppliedField[];
}

interface PreviewPanelProps {
  draft: GenerateSuccess;
  onApply: (accepted: PreviewApplyPayload) => void;
  onRegenerate: (hint?: string) => void;
  onCancel: () => void;
  regenerating?: boolean;
  regenerateError?: GenerateError | null;
  onDismissRegenerateError?: () => void;
  endpointBase?: DideWriterEndpointBase;
  // Restrict which AI fields the host form can actually consume. When a
  // field is omitted here, its checkbox is hidden and that field is never
  // reported in `applied_fields` so the acceptance contract stays honest.
  availableFields?: AppliedField[];
}

const DEFAULT_AVAILABLE_FIELDS: AppliedField[] = [
  'description',
  'title',
  'experience_required',
  'benefits',
];

// HTML for the preview. Prefers the server's `description_html` and falls
// back to rendering the markdown with `marked`. The AI output is untrusted
// (prompt injection), so the HTML always goes through DOMPurify before it
// reaches `dangerouslySetInnerHTML`.
const descriptionHtml = (markdown: string, serverHtml?: string | null): string => {
  if (serverHtml) return DOMPurify.sanitize(serverHtml, { USE_PROFILES: { html: true } });
  try {
    const raw = marked(markdown, { breaks: true, gfm: true });
    const html = typeof raw === 'string' ? raw : '';
    return DOMPurify.sanitize(html, { USE_PROFILES: { html: true } });
  } catch {
    return DOMPurify.sanitize(markdown);
  }
};

export const PreviewPanel: React.FC<PreviewPanelProps> = ({
  draft,
  onApply,
  onRegenerate,
  onCancel,
  regenerating = false,
  regenerateError = null,
  onDismissRegenerateError,
  endpointBase,
  availableFields = DEFAULT_AVAILABLE_FIELDS,
}) => {
  const canApply = useMemo(() => new Set(availableFields), [availableFields]);
  const [applyDescription, setApplyDescription] = useState(canApply.has('description'));
  const [applyTitle, setApplyTitle] = useState(Boolean(draft.title) && canApply.has('title'));
  const [applyExperience, setApplyExperience] = useState(
    Boolean(draft.experience_required) && canApply.has('experience_required')
  );
  const [applyBenefits, setApplyBenefits] = useState(
    draft.benefits.length > 0 && canApply.has('benefits')
  );
  const [hint, setHint] = useState('');

  const html = useMemo(
    () => descriptionHtml(draft.description, draft.description_html),
    [draft.description, draft.description_html]
  );

  // Builds the apply payload from a per-field selection map. Used by both
  // "Apply selected" (current checkbox state) and "Apply all" (every field
  // the host form actually accepts), so the apply contract stays in one
  // place.
  const buildApplyPayload = (selection: {
    description: boolean;
    title: boolean;
    experience: boolean;
    benefits: boolean;
  }): PreviewApplyPayload => {
    const applied_fields: AppliedField[] = [];
    const includeDescription = selection.description && canApply.has('description');
    const includeTitle = selection.title && Boolean(draft.title) && canApply.has('title');
    const includeExperience =
      selection.experience &&
      Boolean(draft.experience_required) &&
      canApply.has('experience_required');
    const includeBenefits =
      selection.benefits && draft.benefits.length > 0 && canApply.has('benefits');

    if (includeDescription) applied_fields.push('description');
    if (includeTitle) applied_fields.push('title');
    if (includeExperience) applied_fields.push('experience_required');
    if (includeBenefits) applied_fields.push('benefits');

    return {
      draft_id: draft.draft_id,
      description: includeDescription ? draft.description : undefined,
      // Hosts sanitize again before inserting (DOMPurify on the job form,
      // Rails `sanitize` on render for admin).
      description_html: includeDescription ? draft.description_html || html || undefined : undefined,
      title: includeTitle ? (draft.title ?? undefined) : undefined,
      experience_required: includeExperience ? (draft.experience_required ?? undefined) : undefined,
      benefits: includeBenefits ? draft.benefits : [],
      applied_fields,
    };
  };

  const handleApplySelected = () => {
    onApply(
      buildApplyPayload({
        description: applyDescription,
        title: applyTitle,
        experience: applyExperience,
        benefits: applyBenefits,
      })
    );
  };

  const handleApplyAll = () => {
    onApply(
      buildApplyPayload({ description: true, title: true, experience: true, benefits: true })
    );
  };

  const benefitLabels = draft.benefits
    .map(b => BENEFIT_OPTIONS.find(o => o.value === b)?.label ?? b)
    .join(', ');

  // Renders its own body and footer so the apply actions sit in the modal's
  // pinned footer while the draft scrolls.
  return (
    <>
      <Modal.Body>
        <div className="d-flex flex-wrap justify-content-between align-items-baseline mb-2" style={{ columnGap: '1rem' }}>
          <h6 className="fw-semibold mb-0">Here's your draft</h6>
          <small className="text-muted">
            Regenerated {draft.regenerate_count} {draft.regenerate_count === 1 ? 'time' : 'times'}
            {typeof draft.tokens === 'number' && ` · ${draft.tokens} tokens`}
          </small>
        </div>

        {regenerateError && (
          <div
            className="alert alert-warning d-flex justify-content-between align-items-center py-2 px-3 mb-2"
            role="alert"
          >
            <span className="small mb-0">{regenerateError.error}</span>
            {onDismissRegenerateError && (
              <button
                type="button"
                className="btn-close"
                aria-label="Dismiss"
                onClick={onDismissRegenerateError}
              />
            )}
          </div>
        )}

        {/* Capped so the apply options and refine box stay on screen
            without scrolling past a long draft. */}
        <div
          className="border rounded-3 p-3 bg-white mb-3"
          style={{ maxHeight: '40vh', overflowY: 'auto', opacity: regenerating ? 0.5 : 1 }}
          role="region"
          tabIndex={0}
          aria-label="Draft description"
          aria-busy={regenerating}
        >
          <div className="dide-preview" dangerouslySetInnerHTML={{ __html: html }} />
        </div>

        <fieldset className="mb-3">
          <legend className="form-label fw-semibold small mb-1">Apply to your job</legend>

          {canApply.has('description') && (
            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                id="dide-apply-desc"
                checked={applyDescription}
                onChange={e => setApplyDescription(e.target.checked)}
              />
              <label className="form-check-label small" htmlFor="dide-apply-desc">
                Replace job description
              </label>
            </div>
          )}

          {draft.title && canApply.has('title') && (
            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                id="dide-apply-title"
                checked={applyTitle}
                onChange={e => setApplyTitle(e.target.checked)}
              />
              <label className="form-check-label small" htmlFor="dide-apply-title">
                Use suggested title: <em>{draft.title}</em>
              </label>
            </div>
          )}

          {draft.experience_required && canApply.has('experience_required') && (
            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                id="dide-apply-exp"
                checked={applyExperience}
                onChange={e => setApplyExperience(e.target.checked)}
              />
              <label className="form-check-label small" htmlFor="dide-apply-exp">
                Use suggested experience line
              </label>
            </div>
          )}

          {draft.benefits.length > 0 && canApply.has('benefits') && (
            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                id="dide-apply-benefits"
                checked={applyBenefits}
                onChange={e => setApplyBenefits(e.target.checked)}
              />
              <label className="form-check-label small" htmlFor="dide-apply-benefits">
                Set benefits: {benefitLabels}
              </label>
            </div>
          )}
        </fieldset>

        <div className="mb-3" data-testid="dide-iterate-group">
          <label htmlFor="dide-regen-hint" className="form-label fw-semibold small mb-1">
            Not quite right? Tell us what to change
          </label>
          <div className="input-group input-group-sm">
            <input
              id="dide-regen-hint"
              type="text"
              className="form-control"
              maxLength={200}
              value={hint}
              placeholder="e.g., make it shorter, emphasize growth"
              onChange={e => setHint(e.target.value)}
              onKeyDown={e => {
                // Skip Enter that confirms an IME candidate (keyCode 229 covers
                // browsers that clear isComposing before keydown fires).
                if (e.nativeEvent.isComposing || e.keyCode === 229) return;
                if (e.key === 'Enter' && !regenerating) {
                  e.preventDefault();
                  onRegenerate(hint || undefined);
                }
              }}
            />
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={() => onRegenerate(hint || undefined)}
              disabled={regenerating}
              data-testid="dide-regenerate"
            >
              {regenerating ? 'Regenerating…' : 'Regenerate'}
            </button>
          </div>
        </div>

        <FeedbackBar draftId={draft.draft_id} endpointBase={endpointBase} />
      </Modal.Body>

      <Modal.Footer
        className="justify-content-between"
        data-testid="dide-decide-group"
        role="group"
        aria-label="Apply or cancel"
      >
        <button
          type="button"
          className="btn btn-link text-muted px-0"
          onClick={onCancel}
          data-testid="dide-cancel"
        >
          Cancel
        </button>
        <div className="d-flex flex-wrap justify-content-end gap-2">
          <button
            type="button"
            className="btn btn-outline-primary"
            onClick={handleApplySelected}
            disabled={regenerating}
            data-testid="dide-apply-selected"
          >
            Apply selected
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleApplyAll}
            disabled={regenerating}
            data-testid="dide-apply-all"
          >
            Apply all
          </button>
        </div>
      </Modal.Footer>
    </>
  );
};

export default PreviewPanel;
